import React, { useEffect, useState } from "react";
import { TeaWordmark } from "@/components/TeaWordmark";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TeaLogo } from "@/components/TeaLogo";
import { colorOf, fmtDay, fmtTime, publicSchApi, schErr } from "@/lib/schApi";

const input = "w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder-white/35 focus:border-[#d4a017] outline-none";
const gold = "px-4 py-2 rounded-sm bg-[#d4a017] text-black font-mono text-[11px] tracking-[0.18em] uppercase font-bold disabled:opacity-40";

function BookForm({ ev, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (confirm_duplicate = false) => {
    setBusy(true);
    try { const d = await publicSchApi.post(`/public/book/${ev.id}`, { ...f, confirm_duplicate }); onDone(d); }
    catch (e) { const d = e?.response?.data?.detail; if (d?.code === "duplicate_warning") setDup(d.message); else toast.error(d?.message || schErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`public-book-form-${ev.id}`} className="mt-3 grid gap-2 sm:grid-cols-3">
      <input data-testid="public-book-name" className={input} placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="public-book-email" type="email" className={input} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <input data-testid="public-book-phone" type="tel" className={input} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      {dup ? (
        <div data-testid="public-book-duplicate" className="sm:col-span-3 text-xs text-amber-200 border border-amber-400/40 bg-amber-500/10 rounded-sm p-2 flex flex-wrap items-center gap-2">
          {dup}<button type="button" onClick={() => submit(true)} data-testid="public-book-duplicate-confirm" className={gold}>Book anyway</button>
        </div>
      ) : (
        <button type="button" disabled={busy || f.name.trim().length < 2 || !f.email.includes("@") || f.phone.trim().length < 7} onClick={() => submit()} data-testid="public-book-submit" className={`${gold} sm:col-span-3 justify-self-start`}>{busy ? "Booking…" : "Confirm booking"}</button>
      )}
    </div>
  );
}

export default function PublicSchedulePage() {
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(null);
  const [done, setDone] = useState(null);
  const load = () => publicSchApi.get("/public/events").then((d) => setEvents(d.events)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);
  return (
    <main data-testid="public-schedule-page" className="min-h-screen bg-[#07080a] text-white px-4 py-10 sm:py-14">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="space-y-2">
          <TeaLogo size={64} testid="public-schedule-logo" />
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]"><TeaWordmark small text="The Exchange Alliance" testid="public-schedule-wordmark" /> · Book a session</div>
          <h1 className="text-3xl sm:text-4xl font-semibold">Orientations, interviews and workshops</h1>
          <p className="text-sm text-white/55">Pick a session and leave your name, email and phone. We'll email your confirmation.</p>
        </header>
        {done && <div data-testid="public-book-success" className="rounded-sm border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-200">You're booked for <b>{done.event.title}</b> on {fmtDay(done.event.start_time)} at {fmtTime(done.event.start_time)}. {done.email_sent ? "A confirmation email is on its way." : "Save this page. Email confirmations will start soon."}</div>}
        {events == null ? <p className="text-white/50">Loading sessions…</p> : !events.length ? <p data-testid="public-schedule-empty" className="text-white/50">No public sessions are open right now. Please check back soon.</p> : (
          <ul data-testid="public-schedule-list" className="space-y-3">
            {events.map((e) => (
              <li key={e.id} data-testid={`public-event-${e.id}`} className="rounded-sm border border-white/10 bg-white/[0.02] p-4 border-l-4" style={{ borderLeftColor: colorOf(e) }}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{e.type_label}</div>
                    <div className="text-lg font-medium">{e.title}</div>
                    <div className="text-sm text-white/55">{fmtDay(e.start_time)} · {fmtTime(e.start_time)} to {fmtTime(e.end_time)} · {e.location || "TBA"}</div>
                    {e.max_capacity && <div className="text-xs text-white/45 mt-0.5">{Math.max(0, e.max_capacity - e.current_enrollment)} of {e.max_capacity} seats left</div>}
                  </div>
                  {e.is_full ? <span data-testid={`public-event-full-${e.id}`} className="font-mono text-[11px] uppercase text-red-300">This session is full</span>
                    : open !== e.id && <button type="button" onClick={() => { setOpen(e.id); setDone(null); }} data-testid={`public-event-book-${e.id}`} className={gold}>Book</button>}
                </div>
                {open === e.id && !e.is_full && <BookForm ev={e} onDone={(d) => { setDone(d); setOpen(null); load(); }} />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

export function PublicScheduleCancelPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [bk, setBk] = useState(null);
  const [err, setErr] = useState("");
  const [cancelled, setCancelled] = useState(false);
  useEffect(() => { publicSchApi.get("/public/booking", { token }).then(setBk).catch((e) => setErr(schErr(e))); }, [token]);
  const cancel = () => publicSchApi.post("/public/cancel", { token }).then(() => setCancelled(true)).catch((e) => setErr(schErr(e)));
  return (
    <main data-testid="public-cancel-page" className="min-h-screen bg-[#07080a] text-white px-4 py-14">
      <div className="max-w-lg mx-auto space-y-4">
        <TeaLogo size={56} testid="public-cancel-logo" />
        {err && <p data-testid="public-cancel-error" className="text-red-300">{err}</p>}
        {bk && (cancelled || bk.status === "cancelled"
          ? <p data-testid="public-cancel-done" className="text-emerald-200">Your booking for {bk.event?.title} is cancelled. Thank you for letting us know.</p>
          : <>
            <p className="text-white/80">Cancel your booking for <b>{bk.event?.title}</b> on {bk.event && `${fmtDay(bk.event.start_time)} at ${fmtTime(bk.event.start_time)}`}?</p>
            <button type="button" onClick={cancel} data-testid="public-cancel-confirm" className={gold}>Cancel my booking</button>
          </>)}
      </div>
    </main>
  );
}
